import type { StatusCustodiaEquipamento, PropriedadeAtivo, OrigemAtivo } from '../../../../models';

/**
 * Definições da aba Ativos (inventário próprio) — ver
 * discovery/SI/02-INVENTARIO-PROPRIO-FASE1.md (§3 identidade, §5 tipos, §6 situação).
 */

/** Tipos de ativo: os das Movimentações (menos periférico/outros, fora do inventário por
 * enquanto) + os de pouco fluxo que só existem aqui. `Equipamento.tipo` é texto livre no
 * banco, então equipamentos antigos com periférico/outros continuam aparecendo. */
export const TIPO_ATIVO_LABELS: Record<string, string> = {
  notebook: 'Notebook',
  macbook: 'Macbook',
  macmini: 'Mac Mini',
  imac: 'iMac',
  monitor: 'Monitor',
  smartphone: 'Smartphone',
  chip: 'Chip (cartão SIM)',
  cartao_acesso: 'Cartão de acesso',
  impressora: 'Impressora',
  periferico: 'Periférico',
  outros: 'Outros',
};

/** Tipos oferecidos no cadastro (periférico/outros ficam de fora — §5). */
export const TIPOS_ATIVO_CADASTRO = [
  'notebook',
  'macbook',
  'macmini',
  'imac',
  'monitor',
  'smartphone',
  'chip',
  'cartao_acesso',
  'impressora',
] as const;

export const TIPOS_COMPUTADOR = ['notebook', 'macbook', 'macmini', 'imac'];
/** Mesma regra da Movimentação (nº série obrigatório pra concluir). */
export const TIPOS_COM_NSERIE_OBRIGATORIO = [...TIPOS_COMPUTADOR, 'smartphone', 'monitor'];
export const TIPOS_COM_SO = [...TIPOS_COMPUTADOR, 'smartphone'];

/** Rótulo do identificador por tipo — no chip, o "Número de inventário" faz o papel do patrimônio. */
export function rotuloPatrimonio(tipo: string | null | undefined): string {
  return tipo === 'chip' ? 'Número de inventário' : 'Patrimônio';
}

/**
 * Situação exibida na aba Ativos — agrupa valores de `statusCustodia` que significam a
 * mesma coisa pra quem olha o inventário (ver StatusCustodiaEquipamento): 'devolvido'
 * (devolução entregue à Raro) conta como Em estoque; 'em_homologacao'/'com_cliente'
 * (Estadia em Cliente) como No Cliente.
 */
export type SituacaoAtivo =
  | 'em_configuracao'
  | 'em_preparo'
  | 'em_estoque'
  | 'em_uso'
  | 'aguardando_devolucao'
  | 'em_transito'
  | 'em_manutencao'
  | 'no_cliente'
  | 'devolvido_cliente'
  | 'inativo';

export const SITUACAO_POR_STATUS: Record<StatusCustodiaEquipamento, SituacaoAtivo> = {
  estoque_raro: 'em_estoque',
  devolvido: 'em_estoque',
  em_transito: 'em_transito',
  em_homologacao: 'no_cliente',
  com_cliente: 'no_cliente',
  com_colaborador: 'em_uso',
  em_configuracao: 'em_configuracao',
  em_preparo: 'em_preparo',
  aguardando_devolucao: 'aguardando_devolucao',
  em_manutencao: 'em_manutencao',
  devolvido_cliente: 'devolvido_cliente',
  inativo: 'inativo',
};

/** Valor gravado em `statusCustodia` quando o SI escolhe a Situação manualmente. */
export const STATUS_POR_SITUACAO: Record<SituacaoAtivo, StatusCustodiaEquipamento> = {
  em_configuracao: 'em_configuracao',
  em_preparo: 'em_preparo',
  em_estoque: 'estoque_raro',
  em_uso: 'com_colaborador',
  aguardando_devolucao: 'aguardando_devolucao',
  em_transito: 'em_transito',
  em_manutencao: 'em_manutencao',
  no_cliente: 'em_homologacao',
  devolvido_cliente: 'devolvido_cliente',
  inativo: 'inativo',
};

export const SITUACAO_ATIVO_LABELS: Record<SituacaoAtivo, string> = {
  em_configuracao: 'Em configuração',
  em_preparo: 'Em preparo',
  em_estoque: 'Em estoque',
  em_uso: 'Em uso',
  aguardando_devolucao: 'Aguardando devolução',
  em_transito: 'Em trânsito',
  em_manutencao: 'Em manutenção',
  no_cliente: 'No Cliente',
  devolvido_cliente: 'Devolvido ao Cliente',
  inativo: 'Inativo',
};

/** Ordem do fluxo (placar/filtros). */
export const SITUACOES_ATIVO: SituacaoAtivo[] = [
  'em_configuracao',
  'em_estoque',
  'em_preparo',
  'em_transito',
  'em_uso',
  'aguardando_devolucao',
  'no_cliente',
  'em_manutencao',
  'devolvido_cliente',
  'inativo',
];

export const SITUACAO_ATIVO_PONTO: Record<SituacaoAtivo, string> = {
  em_configuracao: 'bg-indigo-500',
  em_preparo: 'bg-amber-500',
  em_estoque: 'bg-gray-400',
  em_uso: 'bg-green-500',
  aguardando_devolucao: 'bg-orange-500',
  em_transito: 'bg-blue-500',
  em_manutencao: 'bg-yellow-500',
  no_cliente: 'bg-purple-500',
  devolvido_cliente: 'bg-teal-500',
  inativo: 'bg-red-500',
};

export const SITUACAO_ATIVO_BADGE: Record<SituacaoAtivo, string> = {
  em_configuracao: 'bg-indigo-100 text-indigo-800',
  em_preparo: 'bg-amber-100 text-amber-800',
  em_estoque: 'bg-gray-100 text-gray-700',
  em_uso: 'bg-green-100 text-green-800',
  aguardando_devolucao: 'bg-orange-100 text-orange-800',
  em_transito: 'bg-blue-100 text-blue-800',
  em_manutencao: 'bg-yellow-100 text-yellow-800',
  no_cliente: 'bg-purple-100 text-purple-800',
  devolvido_cliente: 'bg-teal-100 text-teal-800',
  inativo: 'bg-red-100 text-red-700',
};

/** Detalhes escolhíveis por Situação (§6). Em trânsito/No Cliente têm detalhe derivado
 * (destino/estadia), não escolhido. */
export const DETALHES_SITUACAO_ATIVO: Partial<Record<SituacaoAtivo, Record<string, string>>> = {
  em_estoque: {
    casa_raro: 'Casa Raro',
    disponivel: 'Disponível',
    reservada: 'Reservada',
    com_pendencia: 'Com pendência',
    formatacao_preventiva: 'Formatação e preventiva',
  },
  inativo: {
    descarte: 'Descarte',
    extraviado: 'Extraviado',
    falha_erro: 'Falha/Erro',
    nao_localizado: 'Não localizado',
    obsoleto: 'Obsoleto',
    vendido: 'Vendido',
  },
};

export const PROPRIEDADE_ATIVO_LABELS: Record<PropriedadeAtivo, string> = {
  raro: 'Raro',
  cliente: 'Cliente',
};

export const ORIGEM_ATIVO_LABELS: Record<OrigemAtivo, string> = {
  manual: 'Cadastro manual',
  glpi: 'Importado do GLPI',
  ocs: 'OCS',
};

/**
 * Convenção de patrimônio (§3). `propriedade` = o que o prefixo indica; usado só pra
 * alertar (nunca bloquear) quando não bate com a Propriedade escolhida. Ordem importa:
 * prefixos mais longos primeiro.
 */
export const PREFIXOS_PATRIMONIO: Array<{ prefixo: string; descricao: string; propriedade: PropriedadeAtivo }> = [
  { prefixo: 'RLMP', descricao: 'Raro — Apple', propriedade: 'raro' },
  { prefixo: 'RLDL', descricao: 'Raro — Dell', propriedade: 'raro' },
  { prefixo: 'RLLN', descricao: 'Raro — Lenovo', propriedade: 'raro' },
  { prefixo: 'RLSG', descricao: 'Raro — Samsung', propriedade: 'raro' },
  { prefixo: 'RL', descricao: 'Raro', propriedade: 'raro' },
  { prefixo: 'ALG', descricao: 'SWAP', propriedade: 'cliente' },
  { prefixo: 'BP', descricao: 'Banco Pine', propriedade: 'cliente' },
];

/** Tipos cujo identificador segue a convenção RL/BP/ALG — chip, cartão e impressora têm
 * numeração própria, então não geram alerta de prefixo. */
export const TIPOS_COM_PREFIXO_PADRAO = [...TIPOS_COMPUTADOR, 'monitor', 'smartphone'];

/**
 * Sub-abas da aba Ativos por categoria — mesmo formato das sub-abas do Inventário/GLPI
 * (Computadores/Monitores/Telefones). "Outros" só aparece se houver algum ativo que não caia
 * nas demais (ex.: periférico/outros cadastrados antes pelas Movimentações).
 */
export type CategoriaAtivoAba =
  | 'computadores'
  | 'monitores'
  | 'smartphones'
  | 'chips'
  | 'cartoes_acesso'
  | 'impressoras'
  | 'outros';

export const CATEGORIAS_ATIVO_ABA: Array<{ id: CategoriaAtivoAba; label: string; tipos: string[] }> = [
  { id: 'computadores', label: 'Computadores', tipos: TIPOS_COMPUTADOR },
  { id: 'monitores', label: 'Monitores', tipos: ['monitor'] },
  { id: 'smartphones', label: 'Smartphones', tipos: ['smartphone'] },
  { id: 'chips', label: 'Chips', tipos: ['chip'] },
  { id: 'cartoes_acesso', label: 'Cartões de acesso', tipos: ['cartao_acesso'] },
  { id: 'impressoras', label: 'Impressoras', tipos: ['impressora'] },
];

export function categoriaDoTipo(tipo: string | null | undefined): CategoriaAtivoAba {
  return CATEGORIAS_ATIVO_ABA.find((c) => tipo && c.tipos.includes(tipo))?.id ?? 'outros';
}
